import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCompanyData } from "../../context/CompanyDataContext";
import { useTraineeData } from "../../context/TraineeDataContext";
import { fetchTrainee } from "../../helpers/getTraineeVersion";
import { API } from "aws-amplify";
import { v4 as uuid } from "uuid";
import { ITrainingVerified } from "./ITrainingVerified";
import sendWebSocketMessage from "../../services/WebSocket/sendWebSocketMessage/SendWebSocketMessage";

import {
	createCheckIn,
	updateTrainee,
	createTrainee,
} from "../../graphql/mutations";

export default function TrainingVerified({ httpService }: ITrainingVerified) {
	/* ——————————————————— context + params ——————————————————— */
	const { companyData } = useCompanyData() || {};
	const { traineeData } = useTraineeData() || {};
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();
	const {
		companyId: companyIdParam = "",
		regionId: regionIdParam = "",
		locationId: locationIdParam = "",
	} = useParams<{
		companyId: string;
		regionId: string;
		locationId: string;
	}>();

	/* fall‑back to localStorage if context emptied on refresh */
	const verifiedCompany =
		companyData ??
		JSON.parse(localStorage.getItem(`companyData-${locationIdParam}`) || "{}");
	const verifiedTrainee =
		traineeData ??
		JSON.parse(localStorage.getItem(`traineeData-${locationIdParam}`) || "{}");

	useEffect(() => {
		if (!verifiedCompany?.id || !verifiedTrainee?.id) {
			navigate(
				`/checkin/${companyIdParam}/${regionIdParam}/${locationIdParam}`,
				{ replace: true },
			);
		}
	}, [verifiedCompany?.id, verifiedTrainee?.id]);

	const verifiedCompanyData = companyData;
	const verifiedTraineeData = traineeData;
	if (!verifiedCompanyData || !verifiedTraineeData) {
		console.error("Company or Trainee data is null");
		//navigate back to checkin
		navigate(`/checkin/${companyIdParam}/${regionIdParam}/${locationIdParam}`);
		return <div>Data is missing</div>; // Render some error or fallback UI
	}

	/* ——————————————————— submit ——————————————————— */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (isSubmitting) return;
		setIsSubmitting(true);

		try {
			let checkoutObject = {
				trainee_id: verifiedTraineeData.id,
				location_id: locationIdParam,
				region_id: regionIdParam,
				company_id: companyIdParam,
				timestamp: Date.now(), // AWSTimestamp = *seconds*
				type: null,
			};

			httpService
				.postData(`checkin`, checkoutObject) // Assuming this should be a POST request - adjust accordingly
				.then((responseData) => {
					// console.log(responseData);
					//derp
				})
				.catch((error) => {
					console.error("Error during check-in:", error);
				});

			/* 1) history row: type 0 = check‑out */
			await API.graphql({
				query: createCheckIn,
				variables: {
					input: {
						id: uuid(),
						trainee_id: verifiedTrainee.id,
						location_id: locationIdParam,
						timestamp: Math.floor(Date.now() / 1000),
						type: 0,
						trainee_name: verifiedTrainee.display_name,
						trainee_phone: verifiedTrainee.phone,
						trainee_company: verifiedTrainee.display_company,
						visitor_type: verifiedTrainee.visitor_type,
						display_company: verifiedTrainee.display_company,
					},
				},
				authMode: "AWS_IAM",
			});

			/* 2) up‑sert / update Trainee */
			let t = await fetchTrainee(verifiedTrainee.id); // IAM read

			if (!t) {
				// very first sighting in the Amplify table → create skeleton OFF‑SITE
				const { data }: any = await API.graphql({
					query: createTrainee,
					variables: {
						input: {
							id: verifiedTrainee.id,
							first_name: verifiedTrainee.first_name,
							last_name: verifiedTrainee.last_name,
							display_name: verifiedTrainee.display_name,
							display_company: verifiedTrainee.display_company,
							visitor_type: verifiedTrainee.visitor_type,
							phone: verifiedTrainee.phone,
							location_id: locationIdParam,
							checked_in: false,
							onsite_pk: verifiedTrainee,
							timestamp: Math.floor(Date.now() / 1000),
						},
					},
					authMode: "AWS_IAM",
				});
				t = data.createTrainee;
			} else {
				// update existing row – set checked_in: false
				const t = await fetchTrainee(verifiedTrainee.id); // IAM read
				await API.graphql({
					query: updateTrainee,
					variables: {
						input: {
							id: t.id,
							checked_in: false,
							timestamp: (Date.now() / 1000) | 0,
							display_company: verifiedTrainee.display_company,
							visitor_type: verifiedTrainee.visitor_type,
							_version: t._version,
						},
					},
					authMode: "AWS_IAM",
				});
			}

			/* 3) local‑storage clean‑up + redirect */
			[
				`checkedIn-${locationIdParam}`,
				`locationId-${locationIdParam}`,
				`outOfDate-${locationIdParam}`,
				`traineeData-${locationIdParam}`,
				`companyData-${locationIdParam}`,
			].forEach((key) => localStorage.removeItem(key));
		} catch (err) {
			console.error(err);
			setIsSubmitting(false); // allow retry if error
		} finally {
			if (verifiedCompanyData?.id && verifiedTraineeData?.id) {
				navigate(
					`/checkin/${companyIdParam}/${regionIdParam}/${locationIdParam}`,
				);
			}
		}
	};

	/* ——————————————————— ui ——————————————————— */
	return (
		<div className="tervuren-container__column-center bg-primary mh-100 flex-gap-1">
			<div className="tervuren-container__column-center-75">
				<i
					className="training-checkmark-icon"
					aria-hidden="true"
					style={{ color: "#74C386" }}
				/>
				<h1 className="fw-500">Training Active</h1>
				<h2 className="fw-500">
					When you're ready to leave, click the button below
				</h2>
				<p className="two-lined-span text-center">
					You are currently active on‑site at:&nbsp;
					<span className="bold-gray-text">{verifiedCompany.name}</span>
				</p>

				<a
					className="tervuren-button-secondary"
					href={`tel:${verifiedCompany.phone}`}
				>
					{verifiedCompany.phone}
				</a>

				<a
					className="tervuren-button-secondary"
					href={`mailto:${verifiedCompany.email}`}
				>
					{verifiedCompany.email}
				</a>

				<form
					onSubmit={handleSubmit}
					className="tervuren-container__column-center width-100"
				>
					<button
						type="submit"
						disabled={isSubmitting}
						className={`tervuren-button-primary mt-2 ${
							isSubmitting ? "disabled" : ""
						}`}
					>
						{isSubmitting ? "Processing..." : "Check Out"}
					</button>
				</form>
			</div>
		</div>
	);
}
